import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type {
  CreateTaxGroupRequest,
  CreateTaxRequest,
  TaxDto,
  TaxGroupDto,
  UpdateTaxGroupRequest,
  UpdateTaxRequest,
} from './types';

const QK_TAXES = ['taxes'] as const;
const QK_TAX_GROUPS = ['tax-groups'] as const;

// ---- Taxes ----

export function useTaxes() {
  return useQuery({
    queryKey: QK_TAXES,
    queryFn: async () => (await apiClient.get<TaxDto[]>('/api/taxes')).data,
  });
}

export function useCreateTax() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateTaxRequest) =>
      (await apiClient.post<TaxDto>('/api/taxes', request)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_TAXES }),
  });
}

export function useUpdateTax() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, request }: { id: string; request: UpdateTaxRequest }) =>
      (await apiClient.put<TaxDto>(`/api/taxes/${id}`, request)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_TAXES }),
  });
}

export function useDeleteTax() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/taxes/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_TAXES }),
  });
}

// ---- Tax groups ----

export function useTaxGroups() {
  return useQuery({
    queryKey: QK_TAX_GROUPS,
    queryFn: async () => (await apiClient.get<TaxGroupDto[]>('/api/tax-groups')).data,
  });
}

export function useCreateTaxGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateTaxGroupRequest) =>
      (await apiClient.post<TaxGroupDto>('/api/tax-groups', request)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_TAX_GROUPS }),
  });
}

export function useUpdateTaxGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, request }: { id: string; request: UpdateTaxGroupRequest }) =>
      (await apiClient.put<TaxGroupDto>(`/api/tax-groups/${id}`, request)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_TAX_GROUPS }),
  });
}

export function useDeleteTaxGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/tax-groups/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_TAX_GROUPS }),
  });
}
